/**
 * Tax Configuration Schema (v2.0.0) — Phase 1 of Issue #358
 *
 * Single source of truth for the `bat.tax_configurations.config` JSONB
 * payload. Shared between:
 *  - Backend `TaxConfigurationLoaderService` (Phase 2+) which `safeParse`s
 *    every row read from the DB.
 *  - Superadmin UI which uses the schema to validate user edits.
 *  - DB seed migrations (0232+) which construct config objects that round-trip
 *    through this schema.
 *
 * Forward-compat: the top-level `schemaVersion` literal lets the loader detect
 * pre-v2 rows and apply backfill / fallback logic without breaking parse.
 *
 * IMPORTANT: every NEW field added here MUST be marked `.optional()` until a
 * matching `jsonb_set` backfill ships in a follow-up migration. Mandatory
 * fields are limited to those already present in every legacy row.
 */

import { z } from 'zod/v4'

// ─── Schema version sentinel ─────────────────────────────────────────────────

/**
 * Current tax-configurations schema version. Bumped only when the JSONB shape
 * changes in a backward-incompatible way (e.g. removing a required field).
 * Additive changes (new optional fields) do NOT require a bump.
 */
export const TAX_CONFIG_SCHEMA_VERSION = '2.0.0' as const

export type TaxConfigSchemaVersion = typeof TAX_CONFIG_SCHEMA_VERSION

// ─── Enum-like value lists ───────────────────────────────────────────────────

/**
 * Supported cost-basis methods. Mirrors `bat.tax_configurations` constants.
 *
 * `HMRC` is the UK's 3-step matching rule, implemented in
 * `cost-basis/strategies/hmrc.strategy.ts` and dispatched by the engine. It was
 * missing here while every OTHER layer already knew about it — the strategy,
 * `cost-basis/types.ts`, and the seeded GB config, which declares
 * `defaultCostMethod: 'HMRC'`.
 *
 * The omission did not fail loudly. `TaxConfigurationConfigSchema.safeParse`
 * rejected the entire GB row, and `TaxConfigurationLoaderService` answers an
 * unparseable row by falling back to a DIFFERENT YEAR's config — so a GB user
 * asking for 2024 was quietly served another year's rules, and where no other
 * GB year exists the request 500s with "no parseable row for country=GB".
 */
export const COST_METHOD_VALUES = [
  'FIFO',
  'LIFO',
  'HIFO',
  'AVERAGE',
  'SPECIFIC_ID',
  'ACB',
  'HMRC',
] as const
export const CostMethodSchema = z.enum(COST_METHOD_VALUES)
export type CostMethod = z.infer<typeof CostMethodSchema>

/** Taxpayer modes (personal vs business). */
export const TAXPAYER_MODE_VALUES = ['personal', 'business'] as const
export const TaxpayerModeSchema = z.enum(TAXPAYER_MODE_VALUES)
export type TaxpayerMode = z.infer<typeof TaxpayerModeSchema>

/** Exchange-rate sources (FK reference values for `bat.exchange_rate_sources`). */
export const EXCHANGE_RATE_SOURCE_VALUES = [
  'ECB',
  'COINMARKETCAP',
  'BINANCE',
  'MANUAL',
  'IRS',
] as const

/** Boolean key no calculation reads (#2829): accepted on stored rows, absent on new ones. */
const obsoleteFlag = () => z.boolean().optional()

export const ExchangeRateSourceSchema = z.enum(EXCHANGE_RATE_SOURCE_VALUES)
export type ExchangeRateSource = z.infer<typeof ExchangeRateSourceSchema>

/** Confidence threshold for DRIP detection automation. */
export const CONFIDENCE_THRESHOLD_VALUES = ['high', 'medium'] as const
export const ConfidenceThresholdSchema = z.enum(CONFIDENCE_THRESHOLD_VALUES)
export type ConfidenceThreshold = z.infer<typeof ConfidenceThresholdSchema>

/** Attribute-mapping fiscal event types (mirror of accounting domain). */
export const ATTRIBUTE_MAPPING_EVENT_TYPE_VALUES = [
  'capital_gain',
  'income',
  'non_taxable',
  'other_payment',
] as const
export const AttributeMappingEventTypeSchema = z.enum(ATTRIBUTE_MAPPING_EVENT_TYPE_VALUES)

// ─── Atomic primitives ───────────────────────────────────────────────────────

/**
 * One row of a progressive tax bracket.
 * `maxAmount: null` represents the open-ended top bracket.
 */
export const TaxRateBracketSchema = z.object({
  minAmount: z.number().nonnegative(),
  maxAmount: z.number().nonnegative().nullable(),
  rate: z.number().min(0).max(1),
})
export type TaxRateBracket = z.infer<typeof TaxRateBracketSchema>

/**
 * What the report engine does with a fiscal model. The three first are
 * generatable report types; the last two are declaration FAMILIES the platform
 * does not produce (wealth statements, foreign-asset declarations), which the
 * customer selector shows disabled with the reason.
 */
export const TaxModelReportKindSchema = z.enum([
  'full-report',
  'capital-gains',
  'income',
  'wealth-statement',
  'foreign-asset-declaration',
])
export type TaxModelReportKind = z.infer<typeof TaxModelReportKindSchema>

/** Fiscal form/model entry exposed to the superadmin UI and consumer endpoints. */
export const TaxModelSchema = z.object({
  value: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  /**
   * Declared by the superadmin. Without it the customer selector classifies
   * the model by its `value` (a fixed map of the seeded models) and treats an
   * unknown value as a full report — so a «Modelo 720» added in the editor was
   * offered, enabled, as a capital-gains report (measured 28/09/2026).
   */
  reportType: TaxModelReportKindSchema.optional(),
})
export type TaxModel = z.infer<typeof TaxModelSchema>

/** One reporting deadline entry (e.g. "Tax Return on 04-15"). */
export const ReportingDeadlineSchema = z.object({
  type: z.string().min(1),
  date: z.string().min(1),
  description: z.string().min(1),
})
export type ReportingDeadline = z.infer<typeof ReportingDeadlineSchema>

// ─── capitalGainsTax ─────────────────────────────────────────────────────────

/**
 * Capital-gains tax parameters.
 * `holdingPeriod` replaces the `365`-day literal hardcoded across the codebase
 * (refactor category #4).
 */
export const CapitalGainsTaxSchema = z.object({
  shortTerm: z.number().min(0).max(1),
  longTerm: z.number().min(0).max(1),
  holdingPeriod: z.number().int().nonnegative(),
})
export type CapitalGainsTax = z.infer<typeof CapitalGainsTaxSchema>

// ─── exemptions ──────────────────────────────────────────────────────────────

/** Per-country exemption thresholds (e.g. UK annual allowance). */
export const ExemptionsSchema = z.object({
  annualAllowance: z.number().nonnegative().optional(),
  minimumGain: z.number().nonnegative().optional(),
})
export type Exemptions = z.infer<typeof ExemptionsSchema>

// ─── features ────────────────────────────────────────────────────────────────

/** Boolean capability flags per country's tax engine. */
export const FeaturesSchema = z.object({
  supportsFIFO: z.boolean(),
  supportsLIFO: z.boolean(),
  supportsHIFO: z.boolean(),
  supportsACB: z.boolean(),
  supportsSpecificId: z.boolean(),
  // @deprecated #2829: ningún cálculo lo lee; opcional porque una fila nueva puede no llevarlo. Hasta #2829b la
  // interfaz de configuración fiscal y el servicio de configuración todavía lo escriben.
  requiresTransactionDetails: obsoleteFlag(),
})
export type Features = z.infer<typeof FeaturesSchema>

// ─── reportingRequirements ───────────────────────────────────────────────────

/** Country-level reporting obligations. */
export const ReportingRequirementsSchema = z.object({
  deadlines: z.array(ReportingDeadlineSchema),
  requiredForms: z.array(z.string().min(1)),
  minimumReportingThreshold: z.number().nonnegative().optional(),
})
export type ReportingRequirements = z.infer<typeof ReportingRequirementsSchema>

// ─── attributeMappings ───────────────────────────────────────────────────────

/**
 * @deprecated UNUSED — kept only so stored rows that still carry
 * `config.attributeMappings` (e.g. ES 2023/2024) stay valid on read and save.
 *
 * It once claimed to map a transaction-attribute key to its fiscal treatment
 * for THIS country, but no tax-report path ever read it. Tax reports classify
 * every transaction from the global catalogue `bat.transaction_attributes`
 * (super-admin «Atributos»). Writing values here changes nothing; seeds no
 * longer populate it.
 */
export const AttributeMappingSchema = z.object({
  eventType: AttributeMappingEventTypeSchema,
  category: z.string().min(1),
  subCategory: z.string().nullable().optional(),
  isTaxable: z.boolean(),
  requiresCostBasis: z.boolean(),
  displayTypeKey: z.string().optional(),
})
export type AttributeMapping = z.infer<typeof AttributeMappingSchema>

// ─── countryDefaults ─────────────────────────────────────────────────────────

/**
 * Full default tax-engine configuration for a country.
 *
 * These 32 fields used to live in a 40-line if-chain inside
 * `tax-report-orchestrator.service.ts:395-433` (refactor category #1).
 * Now they are seeded per-country into the JSONB column and read via the
 * loader service.
 */
export const CountryDefaultsSchema = z.object({
  costMethod: CostMethodSchema,
  mandatoryCostMethod: z.boolean(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  allowSpecificLotSelection: obsoleteFlag(),
  enableShortLongTerm: z.boolean(),
  longTermThresholdDays: z.number().int().nonnegative(),
  longTermDiscountPercent: z.number().min(0).max(100),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  enableStaking: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  enableMining: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  enableAirdrops: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  enableHardForks: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  enableNFTs: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  enableDeFi: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  enableWrapping: obsoleteFlag(),
  personalLimit: z.number().nonnegative(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  taxableFiatSale: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  taxableCryptoCrypto: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  taxablePayments: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  compensateLossesGains: obsoleteFlag(),
  deductTradingFees: z.boolean(),
  deductGasFees: z.boolean(),
  deductWithdrawalFees: z.boolean(),
  deductMiningStakingExpenses: z.boolean(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  compensateIncome: obsoleteFlag(),
  carryForwardYears: z.number().int().nonnegative(),
  compensationLimit: z.number().nonnegative(),
  annualLimitPercent: z.number().min(0).max(100),
  washSaleEnabled: z.boolean(),
  washSaleDays: z.number().int().nonnegative(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  recognizeInternalTransfers: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  exchangeRateSource: ExchangeRateSourceSchema.optional(),
  baseCurrency: z.string().min(1),
  taxpayerMode: TaxpayerModeSchema,
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  treatAsCapitalIncome: obsoleteFlag(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  economicActivity: obsoleteFlag(),
})
export type CountryDefaults = z.infer<typeof CountryDefaultsSchema>

// ─── optimization ────────────────────────────────────────────────────────────

/**
 * Tax-loss harvesting multi-year projection knobs.
 * `currency` is included for display formatting; tax math itself uses
 * `countryDefaults.baseCurrency`.
 */
export const OptimizationSchema = z.object({
  annualBenefitDecayFactor: z.number().min(0).max(1),
  annualLossExpirationRate: z.number().min(0).max(1),
  largeHarvestWarningThreshold: z.number().nonnegative(),
  currency: z.string().min(1).optional(),
})
export type Optimization = z.infer<typeof OptimizationSchema>

// ─── automation ──────────────────────────────────────────────────────────────

/**
 * Automation pipeline configuration. Replaces the hardcoded `COUNTRY_DEFAULTS`
 * map in `tax-automation-config.service.ts:40-66` (refactor category #6).
 *
 * ⚠️ #2194 RETIRÓ TRES CAMPOS de este objeto: `autoResolveWarnings`,
 * `dripDetection.autoClassify` y `multiCurrency.autoConvertOnGeneration`. No los
 * vuelvas a añadir sin cablear antes la conducta que su etiqueta promete — el
 * porqué, medido, está en el docblock de `report-generation-automations.ts`.
 * Sin migración a propósito: las filas guardadas siguen trayendo las claves y
 * este objeto zod las descarta al parsear.
 */
export const AutomationSchema = z.object({
  preReportValidation: z
    .object({
      enabled: z.boolean(),
      blockOnErrors: z.boolean(),
    })
    .optional(),
  dripDetection: z
    .object({
      enabled: z.boolean(),
      confidenceThreshold: ConfidenceThresholdSchema,
    })
    .optional(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  multiCurrency: z
    .object({
      rateSource: z.enum(['ecb', 'coinmarketcap', 'binance', 'manual']),
    })
    .optional(),
  // @deprecated #2829: ningún cálculo lo lee; se acepta en filas antiguas y no se escribe
  auditLogging: z
    .object({
      logSystemMutations: z.boolean(),
    })
    .optional(),
})
export type Automation = z.infer<typeof AutomationSchema>

// ─── washSale ────────────────────────────────────────────────────────────────

/**
 * Wash-sale rule configuration. Replaces `DEFAULT_WASH_SALE_CONFIG` and the
 * `30`-day literal scattered across 5 sites (refactor category #3).
 */
export const WashSaleSchema = z.object({
  enabled: z.boolean(),
  windowDays: z.number().int().nonnegative(),
  // @deprecated #2829: ningún cálculo lo lee; opcional porque una fila nueva puede no llevarlo. Hasta #2829b la
  // interfaz de configuración fiscal y el servicio de configuración todavía lo escriben.
  autoAdjust: obsoleteFlag(),
})
export type WashSale = z.infer<typeof WashSaleSchema>

// ─── hardFork ────────────────────────────────────────────────────────────────

/**
 * Hard-fork detection + cost-basis carryover rules per jurisdiction.
 *
 * Replaces the hardcoded `lookbackDays = 30` / `lookforwardDays = 30` /
 * `allowCarryover = false` constants in `HardForkProcessor.ts:107-108,344`
 * (refactor category #5, 3rd-pass audit of Issue #358).
 *
 *  - `detectionWindowDays`: number of days BEFORE and AFTER the canonical
 *    fork date used to attribute on-chain receipts to the fork event. US
 *    practice is 30 days; some jurisdictions use longer / shorter windows.
 *  - `allowCarryover`: when `true` the parent asset's holding period
 *    carries over to the forked asset (some EU jurisdictions). US Rev. Rul.
 *    2019-24 sets this to `false`.
 *
 * Both fields are `.optional()` here because legacy rows do not yet carry
 * them; the loader returns `null` on absence so callers can decide whether
 * to fall back, error out, or use a service-level default.
 */
export const HardForkConfigSchema = z.object({
  detectionWindowDays: z.number().int().positive(),
  allowCarryover: z.boolean(),
})
export type HardForkConfig = z.infer<typeof HardForkConfigSchema>

// ─── carryforward ────────────────────────────────────────────────────────────

/**
 * Carryforward expiration rule. `expirationYears: null` means unlimited
 * (US, CA, DE in current law).
 *
 * Replaces the switch in `CarryforwardProcessor.ts:101-117`
 * (refactor category #2).
 */
export const CarryforwardRuleSchema = z.object({
  expirationYears: z.number().int().positive().nullable(),
})
export type CarryforwardRule = z.infer<typeof CarryforwardRuleSchema>

// ─── progressiveSavingsBrackets (Spain) ──────────────────────────────────────

/**
 * Container for the Spain "rentas del ahorro" progressive scale.
 * Kept as an object (not bare array) so future Spain-only knobs can sit next
 * to the brackets without a new top-level key.
 */
export const ProgressiveBracketsSchema = z.object({
  brackets: z.array(TaxRateBracketSchema).min(1),
})
export type ProgressiveBrackets = z.infer<typeof ProgressiveBracketsSchema>

// ─── csvLocale ───────────────────────────────────────────────────────────────

/**
 * CSV export locale: delimiter, separators, dateFormat, plus the full headers
 * translation matrix. Replaces the four hardcoded LOCALE_CONFIG records
 * (~250 lines) in `csv/csv-locale-config.ts` (refactor category #8).
 */
const CsvLocaleSummaryHeadersSchema = z.object({
  reportTitle: z.string(),
  reportName: z.string(),
  taxYear: z.string(),
  country: z.string(),
  currency: z.string(),
  generatedAt: z.string(),
  summarySection: z.string(),
  category: z.string(),
  proceeds: z.string(),
  costBasis: z.string(),
  gainLoss: z.string(),
  total: z.string(),
  // Localized category ROW labels for the summary block (the "Capital Gains" and
  // "Income" rows). OPTIONAL so tax_configurations rows seeded before migration
  // 0289 (which never wrote these keys) still validate. When absent, the CSV
  // (tax-report-csv.service) and XLSX (excel-summary-sheet) exporters fall back to
  // the English literals 'Capital Gains' / 'Income' — preserving prior behavior for
  // any unmigrated locale.
  capitalGains: z.string().optional(),
  income: z.string().optional(),
})

const CsvLocaleTransactionHeadersSchema = z.object({
  date: z.string(),
  type: z.string(),
  asset: z.string(),
  quantity: z.string(),
  platform: z.string(),
  event: z.string(),
  proceeds: z.string(),
  costBasis: z.string(),
  gainLoss: z.string(),
  days: z.string(),
  longTerm: z.string(),
  method: z.string(),
})

const CsvLocaleLotHeadersSchema = z.object({
  asset: z.string(),
  purchaseDate: z.string(),
  consumedAmount: z.string(),
  costBasis: z.string(),
  saleProceeds: z.string(),
  gainLoss: z.string(),
  holdingPeriod: z.string(),
  longTerm: z.string(),
  method: z.string(),
  initialLot: z.string(),
  remainingLot: z.string(),
  unitPrice: z.string(),
})

const CsvLocaleCarryforwardHeadersSchema = z.object({
  taxYear: z.string(),
  lossAmount: z.string(),
  expirationYear: z.string(),
  status: z.string(),
  description: z.string(),
})

export const CsvLocaleSchema = z.object({
  delimiter: z.enum([',', ';']),
  decimalSeparator: z.enum(['.', ',']),
  thousandsSeparator: z.enum([',', '.', '', ' ']),
  dateFormat: z.enum(['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD']),
  booleanTrue: z.string().min(1),
  booleanFalse: z.string().min(1),
  headers: z.object({
    summary: CsvLocaleSummaryHeadersSchema,
    transactions: CsvLocaleTransactionHeadersSchema,
    lots: CsvLocaleLotHeadersSchema,
    carryforward: CsvLocaleCarryforwardHeadersSchema,
  }),
})
export type CsvLocale = z.infer<typeof CsvLocaleSchema>

// ─── formatGenerators ────────────────────────────────────────────────────────

/**
 * One row of the format-generator registry keyed by slug (e.g. "form8949",
 * "modelo100", "hmrc", "generic"). Replaces the hardcoded `FORMAT_CONFIGS`
 * record in `official-tax-format.service.ts:20-108` (refactor category #7).
 */
export const FormatGeneratorSchema = z.object({
  format: z.string().min(1),
  filenameLabel: z.string().min(1).optional(),
  headers: z.array(z.string().min(1)),
  dateFormat: z.string().min(1),
  currency: z.string().min(1),
  decimalSeparator: z.string().min(1),
  thousandsSeparator: z.string(),
})
export type FormatGenerator = z.infer<typeof FormatGeneratorSchema>

// ─── Top-level container ─────────────────────────────────────────────────────

/**
 * Full validated shape of `bat.tax_configurations.config` for schemaVersion 2.0.
 *
 * Required keys = anything that already exists on every legacy row. Anything
 * NEW introduced in Phase 1 stays `.optional()` until `0232a_*_backfill.sql`
 * has populated it everywhere.
 */
export const TaxConfigurationConfigSchema = z.object({
  // Sentinel — gates forward compatibility.
  schemaVersion: z.literal(TAX_CONFIG_SCHEMA_VERSION),

  // Legacy mandatory fields (present on every existing row).
  capitalGainsTax: CapitalGainsTaxSchema,
  exemptions: ExemptionsSchema,
  features: FeaturesSchema,
  currency: z.string().min(1),
  dateFormat: z.string().min(1),
  locale: z.string().min(1),
  // Opcional: una fila nueva no lo lleva; su dueño es #3367 (migra a obligaciones con vigencia); NO obsoleto
  reportingRequirements: ReportingRequirementsSchema.optional(),

  // Optional legacy fields (present on most but not all rows).
  taxRateBrackets: z.array(TaxRateBracketSchema).optional(),
  rateRangeLabel: z.string().optional(),
  models: z.array(TaxModelSchema).optional(),
  defaultModel: z.string().optional(),
  defaultCostMethod: CostMethodSchema.optional(),
  supportedCostMethods: z.array(CostMethodSchema).optional(),
  supportedExchangeRateSources: z.array(ExchangeRateSourceSchema).optional(),
  supportedBaseCurrencies: z.array(z.string().min(1)).optional(),
  supportedExportFormats: z.array(z.string().min(1)).optional(),
  supportedTaxpayerModes: z.array(TaxpayerModeSchema).optional(),
  countryDefaults: CountryDefaultsSchema.optional(),
  optimization: OptimizationSchema.optional(),
  /**
   * @deprecated Ignored by tax reports (classification comes from
   * `bat.transaction_attributes`). Still accepted so existing rows validate;
   * see {@link AttributeMappingSchema}.
   */
  attributeMappings: z.record(z.string(), AttributeMappingSchema).optional(),
  automation: AutomationSchema.optional(),

  // New fields introduced by Phase 1 (#358) — all optional + backfilled in 0232a.
  washSale: WashSaleSchema.optional(),
  carryforward: CarryforwardRuleSchema.optional(),
  hardFork: HardForkConfigSchema.optional(),
  csvLocale: CsvLocaleSchema.optional(),
  formatGenerators: z.record(z.string(), FormatGeneratorSchema).optional(),
  progressiveSavingsBrackets: ProgressiveBracketsSchema.optional(),
  /**
   * UK HMRC annual exempt amount keyed by tax year (e.g. {"2023":12300}).
   * Replaces the `getHMRCAnnualExemptAmount` literal switch
   * in `formats/hmrc.format.ts:41-48` (refactor category #11).
   */
  annualExemptAmountsByYear: z
    .record(z.string().regex(/^\d{4}$/), z.number().nonnegative())
    .optional(),
})

/**
 * The Zod v4 `type` discriminator of a schema, looking through `.optional()`
 * and `.nullable()` wrappers.
 *
 * Reads `def.type` rather than testing `instanceof z.ZodRecord`: a consumer may
 * resolve its own copy of zod, and an `instanceof` across two package instances
 * silently answers false.
 */
function resolveZodTypeName(schema: unknown): string | undefined {
  let current: unknown = schema

  // Bounded walk — `.optional().nullable()` is the deepest nesting the canonical
  // schema uses, and a bound means a malformed schema cannot spin forever.
  for (let depth = 0; depth < 4; depth += 1) {
    const def = (current as { def?: { type?: unknown; innerType?: unknown } } | undefined)?.def
    if (!def || typeof def.type !== 'string') return undefined
    if ((def.type === 'optional' || def.type === 'nullable') && def.innerType !== undefined) {
      current = def.innerType
      continue
    }
    return def.type
  }

  return undefined
}

/**
 * Top-level config keys whose value REPLACES the stored one instead of being
 * merged into it. Today: `formatGenerators`, `annualExemptAmountsByYear` and
 * `attributeMappings` (the last one is deprecated and ignored by tax reports,
 * but still replaces wholesale when a client sends it).
 *
 * WHY (#2254, third facet). A deep merge combines two plain objects key by key,
 * which is right for a fixed-shape block like `csvLocale.headers.summary` — the
 * form does not expose every header, so the untouched ones must survive. It is
 * WRONG for a dictionary whose entries ARE the business rules: the editor sends
 * the complete map, and key-merging it means an entry can be added or changed
 * but NEVER removed. Arrays already replace wholesale for the same reason.
 *
 * DERIVED from the schema, never hand-listed, so a new `z.record(...)` field
 * cannot be added and quietly inherit the merging behaviour that loses
 * deletions. Backend and frontend read the SAME set from here.
 */
export const DICTIONARY_VALUED_TAX_CONFIG_KEYS: ReadonlySet<string> = new Set(
  Object.entries(TaxConfigurationConfigSchema.shape)
    .filter(([, field]) => resolveZodTypeName(field) === 'record')
    .map(([key]) => key),
)

/**
 * The inferred TypeScript type for the JSONB column payload. Consumers should
 * import this rather than redeclaring fields by hand.
 */
export type TaxConfigurationConfig = z.infer<typeof TaxConfigurationConfigSchema>
